import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Typography,
    CircularProgress,
    Box,
    IconButton,
    Alert,
    ThemeProvider,
    createTheme,
} from '@mui/material';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import translations from "../../../constants/purchaseInvoices/translations/supplierInvoiceTranslations";
import EditInvoiceDialogStyles from '../../../styles/purchaseInvoices/invoiceManagement/EditInvoiceDialogStyles';

const cacheRtl = createCache({
    key: 'muirtl',
    stylisPlugins: [prefixer, rtlPlugin],
});

const rtlTheme = createTheme({
    direction: 'rtl',
});

const REQUIRES_ATTENTION_STATUSES = [
    'REQUIRES_ATTENTION',
    'חשבונית להמשך טיפול',
    'שגיאה בח.פ החברה',
    'שם חברה לא תואם',
    'אי-התאמה בשם החברה המזמינה',
    'מידע חסר',
    'שגיאה בעיבוד',
    'לא נמצא ח.פ',
    'שגיאה בהקצאה'
];

const EditInvoiceDialog = ({
    open,
    onClose,
    invoice,
    onSave,
    onReprocessAttention,
    isSaving
}) => {
    const [formData, setFormData] = useState({
        booknum: '',
        supName: '',
        companyName: '',
        companyId: '',
        date: '',
        amount: '',
        allocationNumber: '',
        purchaseOrderNumber: ''
    });

    const [originalData, setOriginalData] = useState(null);
    const [dateError, setDateError] = useState('');

    const requiresAttention = useMemo(() => {
        return REQUIRES_ATTENTION_STATUSES.includes(invoice?.status);
    }, [invoice?.status]);

    const hasChanges = useMemo(() => {
        if (!originalData) return false;
        return Object.keys(formData).some(key => formData[key] !== originalData[key]);
    }, [formData, originalData]);

    const errorInfo = useMemo(() => {
        if (!invoice?.errorDetails) {
            return { canEdit: true, errorType: null };
        }

        const errorCode = invoice.errorDetails.code;
        const errorDetails = invoice.errorDetails;

        const canEdit = errorDetails.canEdit !== false;
        const editableFields = errorDetails.editableFields || [];

        return {
            canEdit,
            errorType: errorCode,
            editableFields,
            config: errorDetails,
            details: errorDetails
        };
    }, [invoice?.errorDetails]);

    useEffect(() => {
        if (invoice) {
            const initialData = {
                booknum: invoice.booknum || '',
                supName: invoice.supplierName || invoice.supName || '',
                companyName: invoice.companyName || '',
                companyId: invoice.companyId || '',
                date: invoice.date || '',
                amount: invoice.amount?.toString() || '',
                allocationNumber: invoice.allocationNumber || '',
                purchaseOrderNumber: invoice.purchaseOrderNumber || ''
            };

            setFormData(initialData);
            setOriginalData(initialData);
        }
    }, [invoice]);

    const handleClose = () => {
        setDateError('');
        onClose();
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'date') {
            const datePattern = /^[\d\/\s]*$/;
            if (!datePattern.test(value) || value.length > 10) {
                return;
            }
            setDateError(value.length > 0 && !validateDate(value) ? 'פורמט לא תקין' : '');
        }

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const validateDate = (dateString) => {
        const regex = /^\d{1,2}\/\d{1,2}\/\d{4}$/;
        return regex.test(dateString);
    };

    const handleSubmit = () => {
        if (!errorInfo.canEdit) {
            return;
        }

        if (formData.date && !validateDate(formData.date)) {
            setDateError('אנא הכנס תאריך בפורמט DD/MM/YYYY');
            return;
        }

        const updatedInvoice = {
            ...invoice,
            booknum: formData.booknum,
            supplierName: formData.supName,
            supName: formData.supName,
            companyId: formData.companyId,
            companyName: formData.companyName,
            date: formData.date,
            amount: formData.amount,
            allocationNumber: formData.allocationNumber,
            purchaseOrderNumber: formData.purchaseOrderNumber
        };

        if (requiresAttention && onReprocessAttention) {
            onReprocessAttention(updatedInvoice);
        } else {
            onSave(updatedInvoice);
        }
    };

    if (!invoice) return null;

    if (!errorInfo.canEdit) {
        return (
            <CacheProvider value={cacheRtl}>
                <ThemeProvider theme={rtlTheme}>
                    <Dialog 
                        open={open} 
                        onClose={handleClose} 
                        fullWidth 
                        maxWidth="sm" 
                        dir="rtl"
                        sx={EditInvoiceDialogStyles.dialog}
                    >
                        <DialogTitle sx={EditInvoiceDialogStyles.dialogTitle}>
                            <Box sx={EditInvoiceDialogStyles.titleBox}>
                                <WarningAmberIcon sx={EditInvoiceDialogStyles.warningIcon} />
                                <Typography variant="h6">
                                    {errorInfo.config?.frontendMessage?.title || 'שגיאה'}
                                </Typography>
                            </Box>
                            <IconButton 
                                onClick={handleClose}
                                sx={EditInvoiceDialogStyles.closeButton}
                            >
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>

                        <DialogContent dividers sx={EditInvoiceDialogStyles.dialogContent}>
                            <Alert severity="error" sx={EditInvoiceDialogStyles.errorAlert}>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    {errorInfo.config?.frontendMessage?.message}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {errorInfo.config?.frontendMessage?.solution}
                                </Typography>
                            </Alert>

                            <Box sx={EditInvoiceDialogStyles.detailsBox}>
                                <Typography variant="body2" sx={EditInvoiceDialogStyles.sectionTitle}>
                                    פרטי החשבונית:
                                </Typography>
                                <Typography variant="body2">
                                    <strong>מספר חשבונית:</strong> {invoice.booknum}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>שם חברה בחשבונית:</strong> {invoice.companyName}
                                </Typography>
                                {errorInfo.details?.prioritySupplierName && (
                                    <Typography variant="body2" color="error">
                                        <strong>שם חברה בהזמנה:</strong> {errorInfo.details.prioritySupplierName}
                                    </Typography>
                                )}
                            </Box>

                            <Alert severity="info" icon={false} sx={EditInvoiceDialogStyles.infoAlert}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    💡 מה לעשות?
                                </Typography>
                                <Box component="ol" sx={EditInvoiceDialogStyles.instructionsList}>
                                    <li>מחקי את החשבונית הזו</li>
                                    <li>ודאי שיש לך חשבונית עם שם החברה הנכון</li>
                                    <li>העלי את החשבונית המעודכנת מחדש</li>
                                </Box>
                            </Alert>
                        </DialogContent>

                        <DialogActions sx={EditInvoiceDialogStyles.dialogActions}>
                            <Button 
                                onClick={handleClose} 
                                variant="contained" 
                                sx={EditInvoiceDialogStyles.successButton}
                            >
                                הבנתי
                            </Button>
                        </DialogActions>
                    </Dialog>
                </ThemeProvider>
            </CacheProvider>
        );
    }

    const renderEditableFields = () => {
        const fields = errorInfo.editableFields || [];

        return (
            <>
                {(fields.includes('supName') || !requiresAttention) && (
                    <Grid item xs={12} sm={6}>
                        <TextField
                            name="supName"
                            label={translations.supplierName || "שם ספק"}
                            fullWidth
                            variant="outlined"
                            margin="normal"
                            value={formData.supName}
                            onChange={handleChange}
                            disabled={isSaving}
                            sx={EditInvoiceDialogStyles.textField}
                        />
                    </Grid>
                )}

                {(fields.includes('companyName') || !requiresAttention) && (
                    <Grid item xs={12} sm={6}>
                        <TextField
                            name="companyName"
                            label="שם החברה המזמינה"
                            fullWidth
                            variant="outlined"
                            margin="normal"
                            value={formData.companyName}
                            onChange={handleChange}
                            disabled={isSaving}
                            sx={EditInvoiceDialogStyles.textField}
                        />
                    </Grid>
                )}

                {fields.includes('companyId') && (
                    <Grid item xs={12} sm={6}>
                        <TextField
                            name="companyId"
                            label="ח.פ החברה"
                            fullWidth
                            variant="outlined"
                            margin="normal"
                            value={formData.companyId}
                            onChange={handleChange}
                            disabled={isSaving}
                            error={true}
                            helperText={
                                errorInfo.details?.expectedId
                                    ? `מספר ח.פ שגוי - המספר הנכון הוא ${errorInfo.details.expectedId}`
                                    : errorInfo.config?.frontendMessage?.helperText || 'מספר ח.פ שגוי - יש להזין את המספר הנכון'
                            }
                            sx={{
                                ...EditInvoiceDialogStyles.textField,
                                ...EditInvoiceDialogStyles.textFieldError
                            }}
                        />
                    </Grid>
                )}

                {fields.includes('allocationNumber') && (
                    <Grid item xs={12} sm={6}>
                        <TextField
                            name="allocationNumber"
                            label="מספר הקצאה"
                            fullWidth
                            variant="outlined"
                            margin="normal"
                            value={formData.allocationNumber}
                            onChange={handleChange}
                            disabled={isSaving}
                            error={true}
                            helperText={errorInfo.config?.frontendMessage?.helperText || 'נדרש למלא מספר הקצאה תקין'}
                            sx={{
                                ...EditInvoiceDialogStyles.textField,
                                ...EditInvoiceDialogStyles.textFieldError
                            }}
                        />
                    </Grid>
                )}

                {fields.includes('purchaseOrderNumber') && (
                    <Grid item xs={12}>
                        <TextField
                            name="purchaseOrderNumber"
                            label="מספר הזמנת רכש (PO)"
                            fullWidth
                            variant="outlined"
                            margin="normal"
                            value={formData.purchaseOrderNumber}
                            onChange={handleChange}
                            disabled={isSaving}
                            error={true}
                            helperText={errorInfo.config?.frontendMessage?.helperText || 'יש להזין את מספר הזמנת הרכש כפי שמופיע בפריוריטי (למשל: PO2510000605)'}
                            sx={{
                                ...EditInvoiceDialogStyles.textField,
                                ...EditInvoiceDialogStyles.textFieldError
                            }}
                        />
                    </Grid>
                )}
            </>
        );
    };

    return (
        <CacheProvider value={cacheRtl}>
            <ThemeProvider theme={rtlTheme}>
                <Dialog
                    open={open}
                    onClose={isSaving ? undefined : handleClose}
                    fullWidth
                    maxWidth="sm"
                    dir="rtl"
                    sx={EditInvoiceDialogStyles.dialog}
                >
                    <DialogTitle sx={EditInvoiceDialogStyles.dialogTitle}>
                        <Typography variant="h6">עריכת חשבונית</Typography>
                        {!isSaving && (
                            <IconButton 
                                aria-label="close" 
                                onClick={handleClose} 
                                size="small"
                                sx={EditInvoiceDialogStyles.closeButton}
                            >
                                <CloseIcon />
                            </IconButton>
                        )}
                    </DialogTitle>

                    <DialogContent dividers sx={EditInvoiceDialogStyles.dialogContent}>
                        {invoice?.errorDetails?.hebrewMessage && requiresAttention && (
                            <Alert
                                severity="warning"
                                sx={EditInvoiceDialogStyles.warningAlert}
                            >
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    בעיה שזוהתה על ידי המערכת:
                                </Typography>
                                <Typography variant="body2">
                                    {invoice.errorDetails.hebrewMessage}
                                </Typography>
                            </Alert>
                        )}

                        <Grid container spacing={3} sx={EditInvoiceDialogStyles.gridContainer}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="booknum"
                                    label="מספר חשבונית"
                                    fullWidth
                                    variant="outlined"
                                    margin="normal"
                                    value={formData.booknum}
                                    onChange={handleChange}
                                    disabled={isSaving}
                                    sx={EditInvoiceDialogStyles.textField}
                                />
                            </Grid>

                            {renderEditableFields()}

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="date"
                                    label={translations.date || "תאריך"}
                                    placeholder="DD/MM/YYYY"
                                    fullWidth
                                    variant="outlined"
                                    margin="normal"
                                    value={formData.date}
                                    onChange={handleChange}
                                    disabled={isSaving}
                                    error={!!dateError}
                                    helperText={dateError || 'פורמט: DD/MM/YYYY'}
                                    sx={dateError ? {
                                        ...EditInvoiceDialogStyles.textField,
                                        ...EditInvoiceDialogStyles.textFieldError
                                    } : EditInvoiceDialogStyles.textField}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="amount"
                                    label={translations.amount || "סכום"}
                                    fullWidth
                                    variant="outlined"
                                    margin="normal"
                                    value={formData.amount}
                                    onChange={handleChange}
                                    disabled={isSaving}
                                    sx={EditInvoiceDialogStyles.textField}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>

                    <DialogActions sx={EditInvoiceDialogStyles.dialogActions}>
                        <Button 
                            onClick={handleClose} 
                            disabled={isSaving}
                            sx={EditInvoiceDialogStyles.cancelButton}
                        >
                            ביטול
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            disabled={isSaving || !hasChanges}
                            startIcon={isSaving ? <CircularProgress size={20} color="inherit" sx={EditInvoiceDialogStyles.loadingIcon} /> : null}
                            sx={{
                                ...EditInvoiceDialogStyles.saveButton,
                                ...(requiresAttention && EditInvoiceDialogStyles.reprocessButton)
                            }}
                        >
                            {isSaving
                                ? (requiresAttention ? 'מעבד מחדש...' : 'שומר...')
                                : (requiresAttention ? 'שמור ועבד מחדש' : 'שמור שינויים')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </ThemeProvider>
        </CacheProvider>
    );
};

export default EditInvoiceDialog;